import strings from '../../../strings/strings.json';
import styled from 'styled-components';
import { breakpoints } from 'theme';
import { ButtonRowRight, Headline3 } from 'components/Elements';
import { FormCard } from 'components/forms/FormComponents';
import TextField from 'components/forms/TextField';
import { createErrorMessage, fieldErrorCodes } from 'shared/error-messages';
import React from 'react';
import Button from 'components/buttons/Button';
import { ResourcesInput } from './activityForms/shared/SharedActivityFormComponents';
import useKeyboardTrap from 'hooks/useKeyboardTrap';

type ResourcesFormProps = {
    closeModal: () => void;
    addResource: (materialResources: ResourcesInput) => void;
};

export function ResourcesForm({ closeModal, addResource }: ResourcesFormProps): JSX.Element {
    const [url, setUrl] = React.useState<string>('');
    const [text, setText] = React.useState<string>('');
    const [linkUrlError, setLinkUrlError] = React.useState<string>('');
    const [textToDisplayError, setTextToDisplayError] = React.useState<string>('');

    const { ref: trapRef } = useKeyboardTrap(closeModal);
    const urlRef = React.useRef<HTMLInputElement>(null);
    const textRef = React.useRef<HTMLInputElement>(null);

    const validateLinkUrl: () => string = () => {
        if (!url) {
            return `${strings.resourcesForm.urlInputLabel} is a required field`;
        }
        const regex =
            /([Hh][Tt][Tt][Pp]([Ss])?:\/\/)?(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+(?:\.(?:[a-zA-Z\u00a1-\uffff0-9]+-?)*[a-zA-Z\u00a1-\uffff0-9]+)*\.[a-zA-Z\u00a1-\uffff]{2,}(?::\d{2,5})?(?:\/[^\s]*)?/;
        const valid = regex.test(url);
        if (!valid) {
            return strings.resourcesForm.invalidLinkUrl;
        } else if (url.length > 400) {
            return createErrorMessage(strings.resourcesForm.urlInputLabel, fieldErrorCodes.maxLength);
        } else {
            return '';
        }
    };

    const validateTextToDisplay: () => string = () => {
        if (!text) {
            return `${strings.resourcesForm.titleInputLabel} is a required field`;
        } else if (text.length > 120) {
            return createErrorMessage(strings.resourcesForm.titleInputLabel, fieldErrorCodes.maxLength);
        } else {
            return '';
        }
    };

    function onSubmit() {
        const urlError = validateLinkUrl();
        const textError = validateTextToDisplay();

        if (urlError || textError) {
            setLinkUrlError(urlError);
            setTextToDisplayError(textError);

            if (urlError) {
                urlRef.current?.focus();
            } else if (textError) {
                textRef.current?.focus();
            }
            return;
        }

        let finalUrl = url;
        if (!finalUrl.startsWith('https://') && !finalUrl.startsWith('http://')) {
            finalUrl = 'https://' + finalUrl;
        }
        addResource({ url: finalUrl, title: text });
    }

    return (
        <Form as="div" ref={trapRef}>
            <Headline3>{strings.resourcesForm.title}</Headline3>
            <div>
                <TextField
                    ref={urlRef}
                    fieldName={'url'}
                    labelText={strings.resourcesForm.urlInputLabel}
                    inputAria={strings.resourcesForm.urlLabelAria}
                    errorMessage={linkUrlError}
                    required
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                    autoFocus
                />
                <TextField
                    ref={textRef}
                    fieldName={'title'}
                    labelText={strings.resourcesForm.titleInputLabel}
                    inputAria={strings.resourcesForm.titleInputLabelAria}
                    errorMessage={textToDisplayError}
                    required
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
                />
            </div>
            <ButtonRowRight>
                <Button
                    title={strings.resourcesForm.cancelButton}
                    aria={strings.resourcesForm.cancelButtonAria}
                    colour={'BLACK'}
                    onClick={closeModal}
                />
                <Button
                    title={strings.resourcesForm.addResourcesButton}
                    aria={strings.resourcesForm.addResourcesButtonAria}
                    onClick={onSubmit}
                />
            </ButtonRowRight>
        </Form>
    );
}

const Form = styled(FormCard)`
    @media (max-width: ${breakpoints.sm}) {
        border: none;
        box-shadow: none;
    }
`;
